"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  detectMetaAttribution,
  ensureMetaPixelInitialized,
  isMetaPixelTrackedPath,
  normalizePathname,
  persistMetaAttribution,
  trackMetaPageView,
} from "@/lib/analytics/metaPixel";

export function MetaPixelTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedNavRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;

    // Detect Meta attribution from URL parameters, cookies, or storage
    const attribution = detectMetaAttribution(searchParams);

    // If fbclid or Meta attribution is present, preserve it for cross-page navigation
    if (attribution?.fbclid || attribution?.fbc) {
      persistMetaAttribution(attribution);
    }

    // Only fire PageView on tracked pages (/login, /estimate-earnings)
    if (!isMetaPixelTrackedPath(pathname)) {
      return;
    }

    // Only fire if Meta attribution is detected
    if (!attribution) {
      return;
    }

    // Deduplicate: fire once per actual navigation/page load, ignore component re-renders
    const normalizedPath = normalizePathname(pathname);
    const navKey = `${normalizedPath}?${searchParams.toString()}`;

    if (lastTrackedNavRef.current === navKey) {
      return;
    }

    lastTrackedNavRef.current = navKey;

    ensureMetaPixelInitialized();
    trackMetaPageView();
  }, [pathname, searchParams]);

  return null;
}
